/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import {
  Button,
  Description,
  FlexContainer,
  Spinner,
  Text,
  theme,
} from "@nordcloud/gnui";
import { UpdateWorkflowInput, Workflow } from "~/generated/graphql";
import { isEmpty } from "~/tools";
import { InlineContacts } from "../../../components/InlineContacts";
import { DisplayText } from "./DisplayText";

type Props = {
  initValues: Workflow["contactPersons"];
  required?: boolean;
  property: string;
  onSubmit: SubmitHandler<UpdateWorkflowInput>;
};

export function WorkflowDetailsInlineContacts({
  initValues,
  required = true,
  property,
  onSubmit,
}: Props) {
  const { handleSubmit, getValues, setValue, register } = useForm();

  const [isEditMode, setIsEditMode] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const doSubmit = async (data: UpdateWorkflowInput) => {
    setIsEditMode(false);
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const [contacts, setContacts] = React.useState<string[]>([]);

  const onAdd = (newPerson: string) => {
    const newContacts = [...contacts, newPerson];
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const onRemove = (deletedPerson: string) => {
    const newContacts = contacts.filter((item) => item !== deletedPerson);
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const defaultContactIds = initValues.map(({ id }) => id);
  const defaultContactNames = initValues.map(({ name }) => name);

  const EditForm = (
    <form css={{ display: "block" }} onSubmit={handleSubmit(doSubmit)}>
      <input
        type="hidden"
        {...register(property, { required })}
        defaultValue={defaultContactIds}
      />
      <FlexContainer>
        <InlineContacts
          contacts={contacts}
          defaultNames={defaultContactNames}
          onAdd={onAdd}
          onRemove={onRemove}
        />
        <Button
          ml={theme.spacing.spacing02}
          type="submit"
          icon="save"
          size="md"
          severity="low"
        >
          Save
        </Button>
        <Button
          ml={theme.spacing.spacing02}
          icon="close"
          size="md"
          severity="low"
          onClick={() => {
            getValues();
            setIsEditMode(false);
          }}
        >
          Cancel
        </Button>
      </FlexContainer>
      <When condition={isEmpty(contacts) && required}>
        <Description>One contact person is required.</Description>
      </When>
    </form>
  );

  if (loading) {
    return (
      <>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" ml={theme.spacing.spacing04}>
          Loading...
        </Text>
      </>
    );
  }

  if (isEditMode) {
    return EditForm;
  }

  return (
    <FlexContainer alignItems="center">
      <DisplayText
        content={initValues.map(({ name }) => name).join(", ")}
        onChange={() => {
          setIsEditMode(true);
          setContacts(defaultContactIds);
        }}
      />
    </FlexContainer>
  );
}
